import { useTracking } from "react-tracking";
import { DASHBOARDS } from "../../../../../shared/lib/routesResolver";
import { useEffect, useState } from "react";
import {
  DASHBOARDS_ACTIONS_PERFORMANCE_FILTER_SELECTABLES,
  DASHBOARDS_ACTIONS_PERFORMANCE_GROUPING_DIMENSION_SELECTABLES,
  DASHBOARDS_ACTIONS_PERFORMANCE_KPIS,
  DASHBOARDS_ACTIONS_PERFORMANCE_METRIC_CHART_SELECTABLES,
  DASHBOARDS_ACTIONS_PERFORMANCE_METRICS_CHART_BY_DIMENSION,
} from "../../../../../shared/lib/apiEndpointsResolver";
import {
  catchFunction,
  getCurrentMonthRange,
} from "../../../../../shared/lib/utils";
import useAxios from "../../../../../hooks/useAxios";
import useLoading from "../../../../../shared/components/loading/useLoading";
import BreadcrumbPanel from "../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  Collapsible,
  CollapsibleContent,
} from "../../../../../shared/components/collapsible/Collapsible";
import FixedScrollArea from "../../../../../shared/components/scroll-area/FixedScrollArea";
import Kpis from "./kpi/Kpis";
import DataContainer from "./data/DataContainer";
import dayjs from "dayjs";
import ActionsPerformanceFilters from "../../shared/ActionsPerformanceFilters";

const ActionsPerformanceDashboardContainer = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [filterSelectables, setFilterSelectables] = useState();
  const [metricsChartSelectables, setMetricsChartSelectables] = useState([]);
  const [dimensionsChartSelectables, setDimensionsChartSelectables] = useState(
    [],
  );
  const [selectedGroupingDimension, setSelectedGroupingDimension] = useState();
  const [selectedTargetMetric, setSelectedTargetMetric] = useState();
  const [kpis, setKpis] = useState();
  const [metricsChartData, setMetricsChartData] = useState();
  const [groupedMetricsChartData, setGroupedMetricsChartData] = useState([]);
  const defaultDateValue = getCurrentMonthRange();
  const [params, setParams] = useState({
    dateRange: defaultDateValue,
    channels: [],
    platforms: [],
    strategies: [],
    clients: [],
    campaigns: [],
    markets: [],
    categories: [],
    products: [],
  });

  const [isOpen, setIsOpen] = useState(true);
  const { trackEvent } = useTracking();

  const breadcrumbItems = [
    {
      title: "actionsPerformance.dashboard.breadcrumb.first",
      link: DASHBOARDS,
    },
    { title: "actionsPerformance.dashboard.breadcrumb.second" },
  ];

  const updateParams = (key, value) => {
    setParams({ ...params, [key]: value });
  };

  const updateClientParams = (value) => {
    if (value.length === 0) {
      setParams({
        ...params,
        clients: [],
        campaigns: [],
        markets: [],
        categories: [],
        products: [],
      });
    } else {
      setParams({ ...params, clients: value });
    }
  };

  const fetchFilterSelectables = () => {
    axios
      .get(DASHBOARDS_ACTIONS_PERFORMANCE_FILTER_SELECTABLES)
      .then((response) => setFilterSelectables(response.data))
      .catch(catchFunction);
  };

  const fetchMetricsChartSelectables = () => {
    axios
      .get(DASHBOARDS_ACTIONS_PERFORMANCE_METRIC_CHART_SELECTABLES)
      .then((response) => setMetricsChartSelectables(response.data))
      .catch(catchFunction);
  };

  const fetchDimensionsChartSelectables = () => {
    axios
      .get(DASHBOARDS_ACTIONS_PERFORMANCE_GROUPING_DIMENSION_SELECTABLES)
      .then((response) => setDimensionsChartSelectables(response.data))
      .catch(catchFunction);
  };

  const fetchGlobalKpis = () => {
    axios
      .post(DASHBOARDS_ACTIONS_PERFORMANCE_KPIS, params)
      .then((response) => setKpis(response.data))
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const fetchGroupedMetricsChartData = (groupingDimension, targetMetric) => {
    if (groupingDimension && targetMetric) {
      setSelectedGroupingDimension(groupingDimension);
      setSelectedTargetMetric(targetMetric);

      if (groupingDimension === "none") {
        setGroupedMetricsChartData([]);
      } else {
        axios
          .post(
            DASHBOARDS_ACTIONS_PERFORMANCE_METRICS_CHART_BY_DIMENSION,
            params,
            { params: { dimension: groupingDimension } },
          )
          .then((response) => {
            setGroupedMetricsChartData(
              response.data.map((x) => {
                const targetDimensionValues = x[`sum_${targetMetric}`];
                return targetDimensionValues.reduce(
                  (acc, curr) => ({
                    ...acc,
                    [curr.first]: curr.second,
                  }),
                  { date: new dayjs(x.date).unix() },
                );
              }),
            );
          })
          .catch(catchFunction);
      }
    }
  };

  useEffect(() => {
    fetchFilterSelectables();
    fetchMetricsChartSelectables();
    fetchDimensionsChartSelectables();
    trackEvent({
      page: "actions-performance-dashboard",
      event: "page-load",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchGlobalKpis();
    if (selectedGroupingDimension && selectedTargetMetric) {
      fetchGroupedMetricsChartData(
        selectedGroupingDimension,
        selectedTargetMetric,
      );
    }
  }, [params]);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <Collapsible
        className="w-full flex flex-row"
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <CollapsibleContent className="w-1/5">
          <FixedScrollArea className="h-[93vh]">
            <ActionsPerformanceFilters
              selectables={filterSelectables}
              defaultDateValue={defaultDateValue}
              onDateChangeValue={(value) => updateParams("dateRange", value)}
              onStrategyChangeValue={(value) =>
                updateParams("strategies", value)
              }
              onChannelChangeValue={(value) => updateParams("channels", value)}
              onPlatformChangeValue={(value) =>
                updateParams("platforms", value)
              }
              onClientChangeValue={(value) => updateClientParams(value)}
              onCampaignChangeValue={(value) =>
                updateParams("campaigns", value)
              }
              onMarketChangeValue={(value) => updateParams("markets", value)}
              onCategoryChangeValue={(value) =>
                updateParams("categories", value)
              }
              onProductChangeValue={(value) => updateParams("products", value)}
            />
          </FixedScrollArea>
        </CollapsibleContent>
        <div
          className={`${isOpen ? "w-4/5" : "w-full"} flex flex-row bg-adq-gray`}
        >
          <div className="w-full bg-adq-gray">
            <div className="flex flex-col p-4 gap-y-4">
              <Kpis kpis={kpis} />
              <DataContainer
                params={params}
                dateRange={params?.dateRange}
                metricsChartData={metricsChartData}
                metricsSelectables={metricsChartSelectables}
                groupedMetricsChartData={groupedMetricsChartData}
                onGroupingDimensionChange={fetchGroupedMetricsChartData}
                dimensionsSelectables={dimensionsChartSelectables}
              />
            </div>
          </div>
        </div>
      </Collapsible>
    </>
  );
};

export default ActionsPerformanceDashboardContainer;
